import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Building2, Mail, Phone, Briefcase } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../foundation/altzero_base/base/components/ui/card';
import { Button } from '../../../foundation/altzero_base/base/components/ui/button';
import { organisationService } from '../services/organisationService';
import type { BvContact } from '../types/billValidationTypes';

type ContactRow = BvContact & { client_name?: string };

function DetailRow({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="flex justify-between py-3 border-b border-gray-50 last:border-0">
      <dt className="text-sm text-muted-foreground">{label}</dt>
      <dd className="text-sm font-medium text-gray-900 text-right">
        {value || <span className="text-gray-300 font-normal">—</span>}
      </dd>
    </div>
  );
}

export function ContactDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [contact, setContact] = useState<ContactRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    const result = await organisationService.getContactById(id);
    if (result.success) {
      setContact(result.data ?? null);
    } else {
      setError(result.error ?? 'Failed to load contact');
    }
    setLoading(false);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="w-full py-6 px-4 sm:px-6 lg:px-8 space-y-4">
        <div className="h-6 w-40 bg-gray-100 animate-pulse rounded" />
        <div className="h-48 bg-gray-100 animate-pulse rounded-xl" />
      </div>
    );
  }

  if (error || !contact) {
    return (
      <div className="flex flex-col items-center justify-center py-32 text-muted-foreground">
        <User className="h-14 w-14 mb-4 opacity-20" />
        <p className="text-base font-medium mb-1">{error ?? 'Contact not found'}</p>
        <Button variant="link" onClick={() => navigate('/bill-validation/contacts')}>
          Back to Contacts
        </Button>
      </div>
    );
  }

  const fullName = `${contact.first_name ?? ''} ${contact.last_name ?? ''}`.trim();

  return (
    <div className="w-full py-6 px-4 sm:px-6 lg:px-8">
      <button
        onClick={() => navigate('/bill-validation/contacts')}
        className="inline-flex items-center text-sm text-muted-foreground hover:text-primary mb-4 transition-colors"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to Contacts
      </button>

      <div className="space-y-6">

        {/* Header */}
        <Card>
          <CardHeader>
            <div className="flex items-start gap-4">
              <div className="bg-primary/10 p-3 rounded-lg">
                <User className="h-6 w-6 text-primary" />
              </div>
              <div>
                <CardTitle className="text-2xl">{fullName}</CardTitle>
                <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground mt-2">
                  {contact.role && (
                    <span className="inline-flex items-center gap-1">
                      <Briefcase className="h-3.5 w-3.5" />
                      {contact.role}
                    </span>
                  )}
                  {contact.client_name && (
                    <button
                      onClick={() => navigate(`/bill-validation/organisations/${contact.client_id}`, {
                      // Come back to this contact, not the client list (job 16).
                      state: { from: { label: 'Back to Contact', to: `/bill-validation/contacts/${contact.id}` } },
                    })}
                      className="inline-flex items-center gap-1 hover:text-primary transition-colors"
                    >
                      <Building2 className="h-3.5 w-3.5" />
                      {contact.client_name}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </CardHeader>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

          {/* Contact Details */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <User className="h-5 w-5 text-primary" />
                Contact Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              <dl>
                <DetailRow label="First Name" value={contact.first_name} />
                <DetailRow label="Last Name" value={contact.last_name} />
                <DetailRow label="Role" value={contact.role} />
                <DetailRow
                  label="Created"
                  value={new Date(contact.created_at).toLocaleDateString('en-AU')}
                />
              </dl>
            </CardContent>
          </Card>

          {/* Communication */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Mail className="h-5 w-5 text-primary" />
                Communication
              </CardTitle>
            </CardHeader>
            <CardContent>
              <dl>
                <DetailRow label="Email" value={contact.email} />
                <DetailRow label="Phone" value={contact.phone} />
              </dl>
              <div className="mt-4 pt-4 border-t border-gray-50 flex gap-3">
                {contact.email && (
                  <a
                    href={`mailto:${contact.email}`}
                    className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
                  >
                    <Mail className="h-4 w-4" />
                    Send Email
                  </a>
                )}
                {contact.phone && (
                  <a
                    href={`tel:${contact.phone}`}
                    className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
                  >
                    <Phone className="h-4 w-4" />
                    Call
                  </a>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Entity */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Building2 className="h-5 w-5 text-primary" />
                Entity
              </CardTitle>
            </CardHeader>
            <CardContent>
              <dl>
                <DetailRow label="Entity" value={contact.client_name} />
              </dl>
              {contact.client_id && (
                <div className="mt-4 pt-4 border-t border-gray-50">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/bill-validation/organisations/${contact.client_id}`, {
                      // Come back to this contact, not the client list (job 16).
                      state: { from: { label: 'Back to Contact', to: `/bill-validation/contacts/${contact.id}` } },
                    })}
                  >
                    <Building2 className="h-4 w-4 mr-1.5" />
                    View Entity
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

        </div>
      </div>
    </div>
  );
}
